import { DateTime } from 'luxon'
import client_config from '@/assets/json/client_config.json'
import client_lookups from '@/assets/json/clientLookups.json'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import router from '@/router'
import axiosInstance from '@/services'
import i18n from '@/i18n/i18n'
import { useAuthStore } from '@/stores/auth'
import { notify } from 'notiwind'

//#region	checkViolatedFiles
function clearWarnings() {
  const formStore = useFormStore()

  Object.assign(formStore.extraMap, {
    showFinesWarningMessage: null,
    showchangeStatusMessage: null,
    finesWarningMessage: null,
    changeStatusMessage: null
  })
}

function resetAndShowError(errorMessage = 'NOT_ELIGIBLE_TO_APPLY') {
  const formStore = useFormStore()
  formStore.form.setFieldValue('personUnifiedNumber', null)
  Object.assign(formStore.draft.request, { personUnifiedNumber: null })

  console.info(`[resetAndShowError] TODO: show error ${errorMessage}`)
}

function getResidencyTransactionType() {
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    issueResidencyServicesWithPersonSponsor,
    issueResidencyServicesWithEstabSponsor,
    residencyServicesWithPersonSponsor,
    residencyServicesWithEstabSponsor,
    issueNewBornResidencyServices
  } = client_config

  const {
    residencyTransactionType: { issue, renew, newBorn }
  } = client_lookups

  const residencyServiceTypes = [
    ...issueResidencyServicesWithPersonSponsor,
    ...issueResidencyServicesWithEstabSponsor
  ]

  const renewResidencyServices = [
    ...residencyServicesWithEstabSponsor,
    ...residencyServicesWithPersonSponsor
  ]

  if (residencyServiceTypes.includes(+serviceId)) {
    return issue
  } else if (renewResidencyServices.includes(+serviceId)) {
    return renew
  } else if (issueNewBornResidencyServices.includes(+serviceId)) {
    return newBorn
  }
}

async function getUserInfoByUnifiedNumber() {
  const formStore = useFormStore()
  const { personUnifiedNumber } = formStore.form.values || formStore.draft.request
  const response = await axiosInstance.get('/api/user/usersInfoByUnifiedNumber', {
    params: {
      unifiednumber: personUnifiedNumber,
      fileStatusId: 0
    }
  })

  if (!response || response?.numberOfRecords === 0 || response?.collection.length === 0) {
    console.error('[getUserInfoByUnifiedNumber] record not found.')

    throw 'NoRecordFound'
  }

  return response
}

async function fetchFinesForViolatedFiles(files = []) {
  const formStore = useFormStore()

  const { personUnifiedNumber } = formStore.form.values || formStore.draft

  const {
    params: { serviceId },
    query: { administrativeRegionId }
  } = router.currentRoute.value

  const promises = files.map(async (file) => {
    const body = {
      serviceTransId: serviceId,
      personUnifiedNumber,
      departmentCode: file.departmentCode,
      serviceCode: file.serviceCode,
      sequenceNumber: file.sequenceNumber,
      serviceYear: file.serviceYear,
      dateOfBirth: null,
      courtJudgementDate: null,
      administrativeRegionId: administrativeRegionId,
      fileModuleId: file.moduleId,
      isBeneficiariesofFinesExemptions: true
    }

    const fines = await axiosInstance.post('/api/fines/draft/calculateFines', body)

    if (fines.finesAmount != 0) {
      resetAndShowError()
    }
  })

  await Promise.all(promises)
}

async function fetchViolatedFiles(params) {
  const response = await axiosInstance.get('/api/bulkRequest/violatedFiles', { params })

  if ((response || []).length === 0) {
    throw 'NoViolatedFiles'
  }

  if (response.some((file) => file.newVisaSequenceNumber)) {
    throw 'changeStatusMessage'
  }

  if (response.some((file) => !file.sequenceNumber)) {
    throw 'NOT_ELIGIBLE_TO_APPLY'
  }

  return response
}

async function checkViolatedFiles() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    countriesOfDisastersServicesWithSponsor,
    countriesOfDisastersServicesWithoutSponsor,
    ServiceTransactions: { isDivorcedOrWidowedService, changeStatus },
    residencyTransactionType: { renew, newBorn, issue },
    fileServiceCode: { workSeekerVisa }
  } = client_lookups

  const {
    isBeneficiariesofFinesExemptions,
    personUnifiedNumber,
    resiDepartmentCode,
    resiYear,
    resiServiceCode,
    resiSequenceNumber,
    isWorkSeekerChangeStatus,
    resiVisaServiceCode
  } = formStore.form.values || formStore.draft

  const {
    enableResidencyViolatorsWithoutFeesServices,
    enableChangeStatsForBeneficiaryOfFinesExemptions
  } = client_config

  const services = [
    ...countriesOfDisastersServicesWithSponsor,
    ...countriesOfDisastersServicesWithoutSponsor,
    changeStatus
  ]

  const isInServicesToAvoid =
    services.includes(+serviceId) || isDivorcedOrWidowedService == serviceId

  const isExempted = isBeneficiariesofFinesExemptions && enableResidencyViolatorsWithoutFeesServices

  const residencyTransactionType = getResidencyTransactionType()
  const transactionTypesToAvoid = [renew, newBorn].includes(residencyTransactionType)

  const shouldSkipFetchingViolatedFiles =
    isWorkSeekerChangeStatus && resiVisaServiceCode == workSeekerVisa

  const earlyReturns = [
    isInServicesToAvoid,
    isExempted && transactionTypesToAvoid,
    shouldSkipFetchingViolatedFiles
  ]

  if (earlyReturns.some((e) => e)) return

  let params = {
    sponsorUnifiedNumber: null,
    sponsorTypeId: null,
    sponsorImmigrationId: null,
    personUnifiedNumber,
    serviceTransId: serviceId,
    fileDepartmentCode: resiDepartmentCode,
    fileSequenceNumber: resiSequenceNumber,
    fileYear: resiYear,
    fileTypeCode: resiServiceCode
  }

  const isExemptedAndIsIssue = isExempted && residencyTransactionType == issue
  if (isExemptedAndIsIssue) {
    try {
      const userInfo = await getUserInfoByUnifiedNumber()
      const [{ fileDepartmentId, fileYear, fileTypeCode, fileSequance }] = userInfo.collection
      Object.assign(params, {
        fileDepartmentCode: fileDepartmentId,
        fileSequenceNumber: fileSequance,
        fileYear,
        fileTypeCode
      })
    } catch (error) {
      resetAndShowError()
      return
    }
  }
  await checkIfVirtualSponsorByUniNumOrFileNumber()
  try {
    const files = await fetchViolatedFiles(params)

    clearWarnings()

    if (
      enableChangeStatsForBeneficiaryOfFinesExemptions &&
      files.some((file) => file.isFileBeneFineExcemption)
    ) {
      Object.assign(formStore.draft.request, {
        isBeneficiariesofFinesExemptions: true
      })

      formStore.form.setFieldValue('isBeneficiariesofFinesExemptions', true)
    }

    if (isExemptedAndIsIssue) {
      clearWarnings()
      await fetchFinesForViolatedFiles(files)
    } else {
      const globalStore = useGlobalStore()

      const finesWarningMessage = globalStore.startedService.multipleRequest
        ? ['existViolatedFiles1', 'existViolatedFiles2']
        : ['existViolatedFilesWithAutoReq1', 'existViolatedFilesWithAutoReq2']

      Object.assign(formStore.extraMap, {
        showFinesWarningMessage: true,
        finesWarningMessage: finesWarningMessage.map((m) => i18n.global.t(m)).join(': ')
      })
    }
  } catch (error) {
    if (error == 'changeStatusMessage') {
      // TODO: handle showing of showing the status message with the error as the t9n key
      return
    }

    if (error == 'NOT_ELIGIBLE_TO_APPLY' || isExemptedAndIsIssue) {
      resetAndShowError()
      return
    }
  }
}
//#endregion

//#region checkExceptionalCase
function setExceptionSponsorTypesSection(value) {
  console.info('[setExceptionSponsorTypesSection] TODO: implement me', value)
}

// aka filterSponsorExceptionTypes
function setExceptionSponsorTypeId(value) {
  const formStore = useFormStore()
  const {
    exceptionSponsorType: { humantarianCase }
  } = client_lookups

  if (value) {
    Object.assign(formStore.draft.request, {
      exceptionSponsorTypeId: humantarianCase
    })
  }

  Object.assign(formStore.draft, { isHumanitarianCaseOptionSelected: value })
}

async function setDisability() {
  const formStore = useFormStore()
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    personUnifiedNumber,
    resiSequenceNumber,
    resiVisaSequenceNumber,
    sequenceNumber,
    resiDepartmentCode,
    resiVisaDepartmentCode,
    departmentCode,
    resiYear,
    resiVisaYear,
    serviceYear,
    resiServiceCode,
    resiVisaServiceCode,
    serviceCode
  } = formStore.form.values || formStore.draft.request

  // const noFileNumber = [
  //   resiSequenceNumber,
  //   resiVisaSequenceNumber,
  //   sequenceNumber,
  //   resiYear,
  //   resiVisaYear,
  //   serviceYear,
  //   resiServiceCode,
  //   resiVisaServiceCode,
  //   serviceCode,
  //   resiSequenceNumber,
  //   resiVisaSequenceNumber,
  //   sequenceNumber
  // ].some((e) => !e)
  // if (noFileNumber || !personUnifiedNumber) return

  const finalDepartmentCode = resiDepartmentCode || resiVisaDepartmentCode || departmentCode
  const finalServiceYear = resiYear || resiVisaYear || serviceYear
  const finalServiceCode = resiServiceCode || resiVisaServiceCode || serviceCode
  const finalSequenceNumber = resiSequenceNumber || resiVisaSequenceNumber || sequenceNumber

  const params = {
    personUnifiedNumber,
    serviceTransactionId: serviceId,
    departmentCode: finalDepartmentCode,
    serviceYear: finalServiceYear,
    serviceCode: finalServiceCode,
    sequenceNumber: finalSequenceNumber
  }
  const isPersonDisabled = await axiosInstance.get('/api/user/getPersonAndDisability', { params })
  if (isPersonDisabled) {
    setExceptionSponsorTypesSection(false)
    Object.assign(formStore.extraMap, {
      exceptionSponsorTypeIsRequired: false,
      referenceRequestNumberRequired: false
    })

    formStore.$patch((state) => {
      state.draft.request.exceptionSponsorTypeId = null
      state.draft.isSponsoredSonAbove25Case = false
    })
  }
}

function setIsSponsoredSonLogic() {
  const formStore = useFormStore()
  const { sponsorRelationshipId, familyRelationshipId, sponsorTypeId, genderId, dateOfBirth } =
    formStore.form.values || formStore.draft.request
  const { enableSonAbove25CasesTypingcenter } = client_config

  if ([sponsorTypeId, genderId, dateOfBirth].some((nd) => !nd)) return false

  const {
    sponsorType: { resident },
    gender: { male },
    relationships: { son }
  } = client_lookups

  const relationTypeId = sponsorRelationshipId || familyRelationshipId

  const isResident = sponsorTypeId == resident
  const isMaleSon = genderId == male && relationTypeId == son
  const dobLxn = DateTime.fromFormat(dateOfBirth, 'dd/MM/yyyy')
  const isAbove25 = dobLxn < DateTime.now().minus({ years: 25 })
  const sonAge = DateTime.now().diff(dobLxn, ['years', 'months', 'days'])

  Object.assign(formStore.extraMap, {
    isResident,
    isMaleSon,
    isAbove25,
    enableSonAbove25CasesTypingcenter,
    isSponsoredAbove25: isResident && isMaleSon && isAbove25 && enableSonAbove25CasesTypingcenter,
    sonAge: sonAge.years.toFixed(0)
  })
}

function setIs119Scenario() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    sponsorRelationshipId,
    familyRelationshipId,
    dateOfBirth,
    currentNationalityId,
    sponsoredEntryStatus
  } = formStore.form.values || formStore.draft.request

  const {
    relationships: { son, daughter },
    serviceTransactions119Case,
    nationality: { syrian },
    entryStatus: { outsideUae }
  } = client_lookups

  const relationTypeId = sponsorRelationshipId || familyRelationshipId

  // aka ageAcceptance
  let isAgeAccepted = false
  if (dateOfBirth) {
    const currentDate = DateTime.now()
    const dobLxn = DateTime.fromFormat(dateOfBirth, 'dd/MM/yyyy')

    const yearDiff = currentDate.diff(dobLxn, 'years').years
    if (yearDiff >= 10 && yearDiff < 18) {
      isAgeAccepted = true
    }
  }

  const isSonOrDaughter = [son, daughter].includes(relationTypeId)
  const isSyrian = currentNationalityId == syrian
  const isOutsideUAE = sponsoredEntryStatus == outsideUae

  Object.assign(formStore.extraMap, {
    is119Scenario:
      isAgeAccepted &&
      isSonOrDaughter &&
      isSyrian &&
      isOutsideUAE &&
      serviceTransactions119Case.includes(+serviceId)
  })
}

async function setFirstDegreeRelativeFlag() {
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const formStore = useFormStore()

  const { sponsorRelationshipId, familyRelationshipId, currentNationalityId, sponsorTypeId } =
    formStore.form.values || formStore.draft.request

  const {
    sponsorType: { shaikh, local, gcc },
    nationality: { comoros },
    relationships: {
      wife,
      son,
      daughter,
      husband,
      father,
      mother,
      wifeSon,
      wifeDaughter,
      husbandSon,
      husbandDaughter
    },
    exceptionalRelationsWithOutHumanCaseService,
    familyMemberGoldenIssueVisa,
    familyMemberGoldenIssueResidency,
    familyMemberGoldenRenewResidency,
    familyMemberGoldenIssueIcaAndResiUnifiedForm,
    familyMemberGoldenRenewIcaAndResiUnifiedForm,
    familyMemberGreenIssueVisa,
    familyMemberGreenIssueResidency,
    familyMemberGreenIssueIcaAndResiUnifiedForm,
    familyMemberGreenRenewIcaAndResiUnifiedForm,
    familyMemberGreenRenewResidency
  } = client_lookups

  const relationTypeId = sponsorRelationshipId || familyRelationshipId
  const isComorian = currentNationalityId == comoros

  if (isComorian && [shaikh, local, gcc].includes(sponsorTypeId)) {
    console.info(
      '[setFirstDegreeRelativeFlag] setting flag because isComorian && [shaikh, local, gcc].includes(sponsorTypeId) is true'
    )
    Object.assign(formStore.extraMap, {
      isSponsoredFirstDegreeRelative: [wife, son, daughter, husband, father, mother].includes(
        relationTypeId
      )
    })
    return
  }

  if (exceptionalRelationsWithOutHumanCaseService.includes(+serviceId)) {
    console.info(
      '[setFirstDegreeRelativeFlag] setting flag because exceptionalRelationsWithOutHumanCaseService is true'
    )
    Object.assign(formStore.extraMap, {
      isSponsoredFirstDegreeRelative: [
        son,
        daughter,
        wife,
        husband,
        wifeSon,
        wifeDaughter,
        husbandSon,
        husbandDaughter
      ].includes(relationTypeId)
    })

    return
  }

  const goldenFamilyMemberServices = [
    ...familyMemberGoldenIssueVisa,
    ...familyMemberGoldenIssueResidency,
    ...familyMemberGoldenRenewResidency,
    ...familyMemberGoldenIssueIcaAndResiUnifiedForm,
    ...familyMemberGoldenRenewIcaAndResiUnifiedForm
  ]

  const greenFamilyMemberServices = [
    ...familyMemberGreenIssueVisa,
    ...familyMemberGreenIssueResidency,
    ...familyMemberGreenIssueIcaAndResiUnifiedForm,
    ...familyMemberGreenRenewIcaAndResiUnifiedForm,
    ...familyMemberGreenRenewResidency
  ]

  const isGoldenFamilyMemberService = goldenFamilyMemberServices.includes(+serviceId)
  const isGreenFamilyMemberService = greenFamilyMemberServices.includes(+serviceId)

  if (isGoldenFamilyMemberService || isGreenFamilyMemberService) {
    console.info(
      '[setFirstDegreeRelativeFlag] setting flag because isGoldenFamilyMemberService || isGreenFamilyMemberService is true'
    )
    Object.assign(formStore.extraMap, {
      isSponsoredFirstDegreeRelative: [son, daughter, wife, husband, father, mother].includes(
        relationTypeId
      )
    })
    return
  }

  if (!formStore.extraMap.familyRelations) {
    formStore.extraMap.familyRelations = await axiosInstance.get('/api/lookup/GetFamilyRelations')
  }

  const familyRelation = formStore.extraMap.familyRelations.find((fr) => fr.id == relationTypeId)
  Object.assign(formStore.extraMap, {
    isSponsoredFirstDegreeRelative: familyRelation && familyRelation.isNeedHumanitarianException
  })

  return
}

async function checkExceptionalCase() {
  const formStore = useFormStore()
  const authStore = useAuthStore()

  const { applicationId } = authStore.user
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    personUnifiedNumber,
    sponsorTypeId,
    currentNationalityId,
    sponsorRelationshipId,
    familyRelationshipId
  } = formStore.form.values || formStore.draft.request

  const relationTypeId = sponsorRelationshipId || familyRelationshipId

  const {
    sponsorType: { shaikh, local, gcc, resident, investor },
    shiekhLocalResidencyServicesHaveHumanitarianCase,
    shiekhLocalVisaServicesHaveHumanitarianCase,
    shiekhLocalIcaAndResiServicesHaveHumanitarianCase,
    shiekhLocalNewBornResidencyServicesHaveHumanitarianCase,
    nationality: { comoros },
    relationships: { son, daughter },
    familyMemberIssueVisa,
    familyMemberIssueResidency,
    familyMemberRenewResidency,
    familyMemberIssueIcaAndResiUnifiedForm,
    familyMemberRenewIcaAndResiUnifiedForm,
    residentOtherServicesHaveHumanitarianCase,
    familyMemberGreenIssueVisa,
    familyMemberGreenIssueResidency,
    familyMemberGreenIssueIcaAndResiUnifiedForm,
    familyMemberGreenRenewIcaAndResiUnifiedForm,
    familyMemberGreenRenewResidency,
    familyMemberGoldenIssueVisa,
    familyMemberGoldenIssueResidency,
    familyMemberGoldenRenewResidency,
    familyMemberGoldenIssueIcaAndResiUnifiedForm,
    familyMemberGoldenRenewIcaAndResiUnifiedForm,
    residentNewBornResidencyServicesHaveHumanitarianCase,
    servicesWithOptionalHumanitarianCase,
    application: { gcc: gccApplicationId, gcc_citizen, resident: residentApplicationId, citizen }
  } = client_lookups

  const { enableHumanitarianForIndividual } = client_config

  const path = `/api/draft/checkExceptionalCase/${personUnifiedNumber}`
  const isExceptional = await axiosInstance.get(path)

  Object.assign(formStore.extraMap, {
    referenceRequestNumberRequired: true,
    isExceptional
  })

  Object.assign(formStore.extraMap, {
    exceptionSponsorTypeIsRequired: false,
    referenceRequestNumberRequired: false
  })

  setIsSponsoredSonLogic()
  const { sonAge } = formStore.extraMap

  if (!isExceptional) {
    await setFirstDegreeRelativeFlag()

    const isHumanitarianCase = [
      shiekhLocalResidencyServicesHaveHumanitarianCase,
      shiekhLocalVisaServicesHaveHumanitarianCase,
      shiekhLocalIcaAndResiServicesHaveHumanitarianCase
    ]

    const isFamilyMemberSerivce = [
      familyMemberIssueVisa,
      familyMemberIssueResidency,
      familyMemberRenewResidency,
      familyMemberIssueIcaAndResiUnifiedForm,
      familyMemberRenewIcaAndResiUnifiedForm
    ]

    const isGreenFamilyMemberService = [
      familyMemberGreenIssueVisa,
      familyMemberGreenIssueResidency,
      familyMemberGreenIssueIcaAndResiUnifiedForm,
      familyMemberGreenRenewIcaAndResiUnifiedForm,
      familyMemberGreenRenewResidency
    ]

    const isGoldenFamilyMemberService = [
      familyMemberGoldenIssueVisa,
      familyMemberGoldenIssueResidency,
      familyMemberGoldenRenewResidency,
      familyMemberGoldenIssueIcaAndResiUnifiedForm,
      familyMemberGoldenRenewIcaAndResiUnifiedForm
    ]

    const isFamilyMemberService = [
      ...isFamilyMemberSerivce,
      ...isGreenFamilyMemberService,
      ...isGoldenFamilyMemberService,
      ...residentOtherServicesHaveHumanitarianCase
    ].includes(+serviceId)

    const isComorian = currentNationalityId == comoros
    const isFirstDegreeRelative = formStore.extraMap.isSponsoredFirstDegreeRelative

    // const isSponsoredSonAbove25AndEnabled =
    setExceptionSponsorTypesSection(false)
    formStore.$patch((state) => {
      state.draft.request.exceptionSponsorTypeId = null
      state.draft.isHumanitarianCaseOptionSelected = false
    })

    if ([shaikh, local, gcc].includes(sponsorTypeId)) {
      if (isHumanitarianCase) {
        if (isFirstDegreeRelative) {
          Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: false })
          setExceptionSponsorTypesSection(isComorian)

          if (isComorian) {
            setExceptionSponsorTypeId(false)
            formStore.form.setFieldValue('exceptionSponsorTypeId', null)
            formStore.$patch((state) => {
              state.draft.request.exceptionSponsorTypeId = null
              state.draft.isHumanitarianCaseOptionSelected = false
            })
          }
        } else {
          setExceptionSponsorTypesSection(true)
          Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: !isComorian })
          setExceptionSponsorTypeId(!isComorian)

          if (isComorian) {
            formStore.$patch((state) => {
              state.draft.request.exceptionSponsorTypeId = null
            })
          }
        }
      } else if (shiekhLocalNewBornResidencyServicesHaveHumanitarianCase.includes(+serviceId)) {
        setExceptionSponsorTypesSection(true)
        setExceptionSponsorTypeId(true)

        if (isComorian) {
          Object.assign(formStore.extraMap, {
            exceptionSponsorTypeIsRequired: false,
            referenceRequestNumberRequired: false,
            isHumanitarianCaseOptionSelected: false
          })

          formStore.form.setFieldValue('exceptionSponsorTypeId', null)
          Object.assign(formStore.draft.request, {
            exceptionSponsorTypeId: null
          })
        } else {
          const isSonOrDaughter = [son, daughter].includes(relationTypeId)
          Object.assign(formStore.extraMap, {
            exceptionSponsorTypeIsRequired: !isSonOrDaughter,
            referenceRequestNumberRequired: !isSonOrDaughter
          })

          Object.assign(formStore.draft, {
            isHumanitarianCaseOptionSelected: !isSonOrDaughter
          })

          formStore.form.setFieldValue('exceptionSponsorTypeId', !isSonOrDaughter)
          Object.assign(formStore.draft.request, {
            exceptionSponsorTypeId: !isSonOrDaughter
          })
        }
      } else {
        setExceptionSponsorTypesSection(false)
        Object.assign(formStore.extraMap, {
          exceptionSponsorTypeIsRequired: false
        })
      }
    } else if ([resident, investor].includes(sponsorTypeId)) {
      if (isFamilyMemberService) {
        if (isFirstDegreeRelative) {
          if (formStore.extraMap.isSponsoredAbove25) {
            if (isGoldenFamilyMemberService) {
              setExceptionSponsorTypesSection(false)

              Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: false })
              Object.assign(formStore.draft, {
                isHumanitarianCaseOptionSelected: false
              })

              const newValues = {
                exceptionSponsorTypeId: true,
                isSponsoredSonAbove25Case: false
              }
              formStore.form.setValues(newValues)
              Object.assign(formStore.draft.request, newValues)
            } else {
              setExceptionSponsorTypesSection(true)
              const newValues = {
                isSponsoredSonAbove25Case: true
              }
              formStore.form.setValues(newValues)
              Object.assign(formStore.draft.request, newValues)

              if (isComorian) {
                Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: false })
                const newValues = {
                  exceptionSponsorTypeId: true,
                  isSponsoredSonAbove25Case: false
                }
                formStore.form.setValues(newValues)
                Object.assign(formStore.draft.request, newValues)
              }
            }
          } else {
            const newValues = {
              isSponsoredSonAbove25Case: false
            }
            formStore.form.setValues(newValues)
            Object.assign(formStore.draft.request, newValues)
            Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: false })

            setExceptionSponsorTypesSection(false)

            if (isComorian) {
              setExceptionSponsorTypesSection(true)
              setExceptionSponsorTypeId(false)
            }
          }
        } else {
          setExceptionSponsorTypesSection(true)
          setExceptionSponsorTypeId(true)
          Object.assign(formStore.extraMap, {
            exceptionSponsorTypeIsRequired: true
          })
        }
      } else {
        const isResident = sponsorTypeId && resident
        if (
          isResident &&
          residentNewBornResidencyServicesHaveHumanitarianCase.includes(+serviceId)
        ) {
          setExceptionSponsorTypesSection(true)
          setExceptionSponsorTypeId(true)
          Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: true })

          // const sonAge
          const isNotSonOrDaugher = [son, daughter].includes(relationTypeId)
          const isSon = sponsorRelationshipId == son
          const isDaughter = sponsorRelationshipId == daughter

          if (isNotSonOrDaugher || (isSon && sonAge >= 25)) {
            Object.assign(formStore.extraMap, {
              exceptionSponsorTypeIsRequired: true,
              referenceRequestNumberRequired: true
            })
          }

          if (isSon && sonAge < 25 && isDaughter) {
            formStore.$patch((state) => {
              state.draft.request.exceptionSponsorTypeId = null
              state.draft.isHumanitarianCaseOptionSelected = false
            })

            Object.assign(formStore.extraMap, {
              exceptionSponsorTypeIsRequired: false,
              referenceRequestNumberRequired: false
            })
          }
        } else {
          setExceptionSponsorTypesSection(false)
          Object.assign(formStore.extraMap, { exceptionSponsorTypeIsRequired: false })
        }
      }
    } else {
      setExceptionSponsorTypesSection(false)
      Object.assign(formStore.extraMap, {
        exceptionSponsorTypeIsRequired: false
      })
    }
  }

  if (servicesWithOptionalHumanitarianCase.includes(+serviceId) && sonAge >= 25) {
    const {
      personUnifiedNumber,
      resiServiceCode,
      resiDepartmentCode,
      resiYear,
      resiSequenceNumber
    } = formStore.form.values || formStore.draft.request

    const params = {
      personUnifiedNumber,
      resiServiceCode,
      resiDepartmentCode,
      resiYear,
      resiSequenceNumber
    }

    const isSponsorInvestorOrPartner = await axiosInstance.get(
      '/api/user/checkIsSponsorInvestorOrPartner',
      { params }
    )

    if (isSponsorInvestorOrPartner) {
      setExceptionSponsorTypesSection(true)
      setExceptionSponsorTypeId(true)
      Object.assign(formStore.extraMap, {
        exceptionSponsorTypeIsRequired: true,
        referenceRequestNumberRequired: true
      })
    }
  }

  if (
    !relationTypeId &&
    shiekhLocalNewBornResidencyServicesHaveHumanitarianCase.includes(+serviceId) === false
  ) {
    setExceptionSponsorTypesSection(false)
    Object.assign(formStore.extraMap, {
      exceptionSponsorTypeIsRequired: false,
      humanitarianExceptionIsRequired: null
    })
  }

  setIs119Scenario()
  if (formStore.extraMap.is119Scenario) {
    setExceptionSponsorTypesSection(true)
    setExceptionSponsorTypeId(true)
    Object.assign(formStore.extraMap, {
      exceptionSponsorTypeIsRequired: true
    })
  }
  const isOneOfThese = [gccApplicationId, gcc_citizen, residentApplicationId, citizen].includes(
    applicationId
  )

  const { isHumanitarianCaseOptionSelected } = formStore.draft

  if (isOneOfThese && enableHumanitarianForIndividual && isHumanitarianCaseOptionSelected) {
    setExceptionSponsorTypesSection(false)
    Object.assign(formStore.extraMap, {
      exceptionSponsorTypeIsRequired: false,
      referenceRequestNumberRequired: false
    })
  }

  const { isMaleSon, isAbove25 } = formStore.extraMap
  if (isMaleSon && isAbove25) {
    // TODO: scope.checkPersonDisability
    await setDisability()
  }
}
//#endregion

//#region updatePersonInformation
function updatePersonInformation() {
  const formStore = useFormStore()
  const {
    residencyBusinessServices: { years10, years5 }
  } = client_lookups

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  if ([years10, years5].includes(+serviceId) === false) return

  const { personUnifiedNumber, englishName, arabicName } =
    formStore.form.values || formStore.form.draft

  formStore.form.setValues({
    sponsorNumber: personUnifiedNumber,
    sponsorEnglishName: englishName,
    sponsorArabicName: arabicName
  })
}
//#endregion

//#region getPersonInfoForCheckUnifiedNumber
async function getPersonInfoForCheckUnifiedNumber() {
  const formStore = useFormStore()

  const { personUnifiedNumber, isBeneficiariesofFinesExemptions } =
    formStore.form.values || formStore.form.draft
  if (!personUnifiedNumber) return

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    isCountriesofDisastersService,
    isRenewCountriesofDisastersService,
    enableResidencyViolatorsWithoutFeesServices,
    issueResidencyServicesWithPersonSponsor,
    issueResidencyServicesWithEstabSponsor,
    residencyServicesWithPersonSponsor,
    residencyServicesWithEstabSponsor,
    issueNewBornResidencyServices
  } = client_config

  const {
    ServiceTransactions: {
      isDivorcedOrWidowedService,
      isDivorcedOrWidowedFamilyService,
      isDivorcedOrWidowedRenew,
      isDivorcedOrWidowedFamilyRenew,
      isDivorcedOrWidowedNewBorn
    },
    module: { residency },
    residencyTransactionType: { issue, renew, newBorn },
    residencyBusinessServices: { years10, years5 },
    residencyBusinessServices: { familyYears10, familyYears5 }
  } = client_lookups

  const issueResidencyService = [
    ...issueResidencyServicesWithPersonSponsor,
    ...issueResidencyServicesWithEstabSponsor
  ]

  const renewResidencyService = [
    ...residencyServicesWithPersonSponsor,
    ...residencyServicesWithEstabSponsor
  ]

  let residencyServiceType
  if (issueResidencyService.includes(+serviceId)) {
    residencyServiceType = issue
  } else if (renewResidencyService.includes(+serviceId)) {
    residencyServiceType = renew
  } else if (issueNewBornResidencyServices.includes(+serviceId)) {
    residencyServiceType = newBorn
  }

  const params = {
    unifiednumber: personUnifiedNumber,
    fileStatusId: 0
  }
  const response = await axiosInstance.get('/api/user/usersInfoByUnifiedNumber', { params })

  if (!response || response?.numberOfRecords <= 0 || (response?.collection ?? []).length <= 0)
    return

  const [
    { isOutsideCountry, fileModuleId, mohleBenifetFlag, isCancelledFile, hasUaePassportFlag }
  ] = response.collection

  /**
   * To make sure that the sponsored is inside the country & the sponsored
   * should has an expired but not cancelled file number
   * (Countries of Disasters Service Only)
   */
  if (isCountriesofDisastersService || isRenewCountriesofDisastersService) {
    if (!isOutsideCountry) {
      Object.assign(formStore.extraMap, { nextButtonIsDisabled: false })
      return
    }

    /**
     * The user can't complete the Residency application, disable/remove
     * the next button and dispose the response object.
     */
    formStore.form.setFieldValue('personUnifiedNumber', null)
    console.info('[getPersonInfoForCheckUnifiedNumber] TODO: show popup NOT_ELIGIBLE_TO_APPLY')
    return
  }

  /**
   * To make sure that the sponsored is inside the country & the sponsored
   * should has an expired but not cancelled file number
   * (Divorced Or Widowed Service Only)
   */
  const isDivorcedServices = [
    isDivorcedOrWidowedService,
    isDivorcedOrWidowedFamilyService,
    isDivorcedOrWidowedRenew,
    isDivorcedOrWidowedFamilyRenew,
    isDivorcedOrWidowedNewBorn
  ].includes(+serviceId)
  if (isDivorcedServices) {
    if (
      serviceId != isDivorcedOrWidowedService ||
      (!isOutsideCountry && fileModuleId == residency)
    ) {
      Object.assign(formStore.extraMap, { nextButtonIsDisabled: mohleBenifetFlag })

      if (isBeneficiariesofFinesExemptions && !mohleBenifetFlag) {
        formStore.form.setFieldValue('personUnifiedNumber', null)
        console.info('[getPersonInfoForCheckUnifiedNumber] TODO: show popup NOT_ELIGIBLE_TO_APPLY')
      }
    }
  }

  /**
   * The user can't complete the Visa application, disable/remove the next button
   * and dispose the response object.
   */
  if (
    isBeneficiariesofFinesExemptions &&
    enableResidencyViolatorsWithoutFeesServices &&
    residencyServiceType == renew &&
    (!mohleBenifetFlag || fileModuleId != residency || isOutsideCountry || isCancelledFile)
  ) {
    formStore.form.setFieldValue('personUnifiedNumber', null)
    console.info('[getPersonInfoForCheckUnifiedNumber] TODO: show popup NOT_ELIGIBLE_TO_APPLY')
  }

  /**
   * This validation ONLY for Businessman\Businesswoman Residency Services
   * AND The Family Of Businessman\Businesswoman
   */
  if (
    [years10, years5, familyYears10, familyYears5].includes(+serviceId) &&
    (isOutsideCountry || !isCancelledFile || !hasUaePassportFlag)
  ) {
    formStore.form.setFieldValue('personUnifiedNumber', null)
    Object.assign(formStore.extraMap, { nextButtonIsDisabled: false })
    console.info('[getPersonInfoForCheckUnifiedNumber] TODO: show popup NOT_ELIGIBLE_TO_APPLY')
  }

  if (
    isBeneficiariesofFinesExemptions &&
    enableResidencyViolatorsWithoutFeesServices &&
    residencyServiceType == newBorn &&
    (!mohleBenifetFlag || !fileModuleId || isOutsideCountry)
  ) {
    formStore.form.setFieldValue('personUnifiedNumber', null)
    console.info('[getPersonInfoForCheckUnifiedNumber] TODO: show popup NOT_ELIGIBLE_TO_APPLY')
  }
}
//#engregion

//#region getIdentityNumberOrUnifiedNumber
async function getIdentityNumberOrUnifiedNumber(isUnifiedNumber, identityNumberOrUnifiedNumber) {
  const formStore = useFormStore()

  const params = {
    isUnifiedNumber,
    identityNumberOrUnifiedNumber
  }
  try {
    const response = await axiosInstance.get(
      '/api/establishment/usersInfoByIdentityNumberOrUnifiedNumber',
      { params }
    )
    if (response?.unifiednumber) {
      formStore.form.setFieldValue('personUnifiedNumber', response.unifiednumber)
    }

    if (response?.identityCardNumber) {
      formStore.form.setFieldValue('identityNumber', response.identityCardNumber)
    }
  } catch (error) {
    formStore.form.setValues({
      personUnifiedNumber: null,
      identityNumber: null
    })
  }
}
//#endregion

//#region getVisaInfo
async function getVisaInfo() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    ServiceTransactions: {
      issueResidencyForExperiencedWorkersInsideUAE,
      issueResidencyForSkilledWorkersOutsideUae,
      issueResidencyForFreelancersOutsideUae
    }
  } = client_lookups

  const {
    personUnifiedNumber,
    resiVisaDepartmentCode,
    resiVisaYear,
    resiVisaServiceCode,
    resiVisaSequenceNumber
  } = formStore.form.values || formStore.draft.request

  const hasFileNumber = [
    resiVisaDepartmentCode,
    resiVisaYear,
    resiVisaServiceCode,
    resiVisaSequenceNumber
  ].every((e) => !!e)

  const inTransactions = [
    issueResidencyForExperiencedWorkersInsideUAE,
    issueResidencyForSkilledWorkersOutsideUae,
    issueResidencyForFreelancersOutsideUae
  ].includes(+serviceId)

  const validInputs = !!personUnifiedNumber || hasFileNumber

  if (!inTransactions || !validInputs) return
  const payload = {
    personUnifiedNumber,
    fileDepartmentCode: resiVisaDepartmentCode,
    fileYear: resiVisaYear,
    fileTypeCode: resiVisaServiceCode,
    fileSequance: resiVisaSequenceNumber,
    serviceTransactionId: serviceId,
    fileModuleId: client_lookups.module.visa
  }

  const response = await axiosInstance.post('/api/typingCenter/request/visa/info/', payload)

  if (!response) return

  const { monthlyIncome, professionId, educationLevelId, annualIncome } = response

  formStore.form.setValues({
    monthlyIncome,
    professionId,
    educationLevelId,
    annualIncome
  })

  Object.assign(formStore.extraMap, {
    disableMonthlyIncome: !!monthlyIncome,
    disableEducationLevelId: !!educationLevelId,
    disableProfession: !!professionId,
    hasAnnualIncome: !!annualIncome
  })
}
//#endregion

//#region getProfessionIdFromVisaInfo
async function getProfessionIdFromVisaInfo() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    ServiceTransactions: {
      decree28_ResidencyPersonsWhoAreTalented,
      longTermResiScientistsAndSpecialists,
      goldenResidenceforHumanitarianPioneersIssueResidence
    }
  } = client_lookups

  if (
    [
      decree28_ResidencyPersonsWhoAreTalented,
      longTermResiScientistsAndSpecialists,
      goldenResidenceforHumanitarianPioneersIssueResidence
    ].includes(+serviceId)
  )
    return

  const {
    personUnifiedNumber,
    resiVisaDepartmentCode,
    resiVisaYear,
    resiVisaServiceCode,
    resiVisaSequenceNumber
  } = formStore.form.values || formStore.draft.request

  const hasFileNumber = [
    resiVisaDepartmentCode,
    resiVisaYear,
    resiVisaServiceCode,
    resiVisaSequenceNumber
  ].every((e) => !!e)

  if (!hasFileNumber) return

  const fileNumber = [
    resiVisaDepartmentCode,
    resiVisaYear,
    ('0' + resiVisaServiceCode).slice(-2),
    ('000000' + resiVisaSequenceNumber).slice(-7)
  ].join()

  const response = await axiosInstance.get('/api/individual/visa/getProfessionIdFromVisaInfo', {
    params: {
      fileNumber,
      personUnifiedNumber
    }
  })

  formStore.form.setFieldValue('professionId', response.professionId)
}
//#endregion

async function processBeneficiaryUnifiedNumber57() {
  const formStore = useFormStore()

  const { personUnifiedNumber } = formStore.form.values || formStore.draft.request
  if (!personUnifiedNumber) return

  await checkExceptionalCase()
  await checkViolatedFiles()
  updatePersonInformation()
  await getPersonInfoForCheckUnifiedNumber()
  await getIdentityNumberOrUnifiedNumber(true, personUnifiedNumber)
  await getVisaInfo()
  await getProfessionIdFromVisaInfo()
}

//#region filterDivorcedRelations
function filterDivorcedRelations(options) {
  const formStore = useFormStore()

  Object.assign(formStore.extraMap, { familyRelationships: options })

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    ServiceTransactions: {
      isDivorcedOrWidowedFamilyService,
      isDivorcedOrWidowedFamilyRenew,
      isDivorcedOrWidowedNewBorn
    },
    relationships: { son, daughter }
  } = client_lookups

  if (
    [
      isDivorcedOrWidowedFamilyService,
      isDivorcedOrWidowedFamilyRenew,
      isDivorcedOrWidowedNewBorn
    ].includes(+serviceId) === false
  )
    return options

  return options.filter((o) => [son, daughter].includes(o.id))
}
//#endregion

//#region handleFamilyRelationChange57
function setResidencyAndUnifiedFormNumberOfYearsForCitizenSponsor() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    sponsorRelationshipId,
    currentNationalityId,
    exceptionSponsorTypeId,
    referenceRequestNumber
  } = formStore.form.values || formStore.draft.request

  const { isInCompleteDraftFlag, numberOfYears } = formStore.extraMap

  const {
    residencyAndUnifiedFormServicesForCitizenSponsor,
    numberOfYears: { oneYear, twoYear, threeYear, fourYear, fiveYear },
    relationships: { son, daughter, wife, husband, father, mother },
    nationality: { comoros }
  } = client_lookups

  const isInService = residencyAndUnifiedFormServicesForCitizenSponsor.includes(serviceId)
  if (isInCompleteDraftFlag || !isInService) return

  const is3or4 = numberOfYears.some((y) => [threeYear, fourYear].includes(y))
  const isSonOrDaughter = [son, daughter].includes(sponsorRelationshipId)
  const isComorian = currentNationalityId == comoros

  const isTheComorianNonOfThese = [son, daughter, wife, husband, father, mother]

  Object.assign(formStore.extraMap, { isNumberOfYearsDisabled: is3or4 && isSonOrDaughter })

  Object.assign(formStore.extraMap, { isNumberOfYearsDisabled: true })
  if (isComorian && isTheComorianNonOfThese) {
    formStore.form.setFieldValue(
      'numberOfYears',
      exceptionSponsorTypeId && referenceRequestNumber ? oneYear : twoYear
    )
  } else {
    formStore.form.setFieldValue(
      'numberOfYears',
      exceptionSponsorTypeId && referenceRequestNumber ? oneYear : fiveYear
    )
  }
}

async function handleFamilyRelationChange57() {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const { sponsorRelationshipId, sponsorGenderId } =
    formStore.form.values || formStore.draft.request
  const { familyRelationships } = formStore.extraMap
  const { residencyServicesForFamilyMembers } = client_config
  const {
    relationships: { son, daughter }
  } = client_lookups

  const setGenderValue = () => {
    if (!sponsorRelationshipId) {
      formStore.form.setFieldValue('genderId', null)
      Object.assign(formStore.extraMap, { isGenderDisabled: false })
      return
    }

    const relationObj = familyRelationships.find((fr) => fr.id == sponsorRelationshipId)

    if (relationObj?.genderId) {
      formStore.form.setFieldValue('genderId', relationObj.genderId)
      Object.assign(formStore.extraMap, { isGenderDisabled: true })
      return
    }

    formStore.form.setFieldValue('genderId', null)
    Object.assign(formStore.extraMap, { isGenderDisabled: false })
  }

  setGenderValue()

  setResidencyAndUnifiedFormNumberOfYearsForCitizenSponsor()

  if (!sponsorRelationshipId) return

  const isSonOrDaughter = [son, daughter].includes(sponsorRelationshipId)
  let showFatherInformation = false
  let showMotherInformation = false
  if (isSonOrDaughter && residencyServicesForFamilyMembers.includes(serviceId)) {
    showFatherInformation = sponsorGenderId == 2
    showMotherInformation = sponsorGenderId == 1
  }

  Object.assign(formStore.extraMap, {
    showFatherInformation,
    showMotherInformation
  })

  if (!formStore.draft.requestNumber) {
    Object.assign(formStore.draft, {
      isParentInsideUae: null,
      eiDorUN: null,
      fatherUnifiedNumber: null,
      fatherEID: null,
      fatherEnglishFullName: null,
      fatherArabicFullName: null,
      fatherDateOfBirth: null,
      fatherPassportNumber: null,
      fatherNationalityId: null,
      motherUnifiedNumber: null,
      motherEID: null,
      motherEnglishFullName: null,
      motherArabicFullName: null,
      motherDateOfBirth: null,
      motherPassportNumber: null,
      motherNationalityId: null
    })
  }

  if (formStore.extraMap.isInCompleteDraftFlag) {
    Object.assign(formStore.extraMap, { disableParentInfo: false })
  }
}
//#endregion

//#region checkPersonInfo
async function checkPersonInfo() {
  const formStore = useFormStore()
  const response = await axiosInstance.get(
    '/api/typingCenter/request/visa/CheckIdentityNumberIsValid',
    {
      params: {
        identityNumber: formStore.form.values.sponsorIdentityNumber
      }
    }
  )
  if (!response) {
    notify(
      {
        group: 'error',
        title: '',
        text: i18n.global.t('IdentityNumberErrorMessage')
      },
      6000
    )
    formStore.draft.request.sponsorIdentityNumber = null
  }
}
//#endregion

//#region setSponsorType
async function setSponsorType(options) {
  const formStore = useFormStore()

  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    virtualResidencyServices,
    sponsorType: {
      resident,
      SponsorCompany,
      SponsorUnionGovernment,
      SponsorLocalGovernment,
      SponsorSemiGovernment,
      AnimalitySponsor,
      AgriculturalSponsor,
      FreeZone
    },
    ServiceTransactions: {
      residencyService,
      residencyFamilyMembersOfTheSponsorWorkingInPrivateSectorOrFreeZone,
      issueResidencyWorkingForUaeCitizenOrGccCitizenSponsores,
      ResidencyDataModification,
      changeStatus,
      VisaDataModification,
      countriesOfDisastersInvestor,
      ResidencyForNewBornAndSponsorTypeInvestor
    },
    familyInvestorServices: { issueResidence, renewResidence },
    countriesOfDisastersServicesWithSponsor
  } = client_lookups

  const typingCenterServices = [
    ResidencyDataModification,
    changeStatus,
    VisaDataModification,
    ...countriesOfDisastersServicesWithSponsor
  ]
  const residentServices = [
    ...virtualResidencyServices,
    residencyService,
    residencyFamilyMembersOfTheSponsorWorkingInPrivateSectorOrFreeZone,
    issueResidencyWorkingForUaeCitizenOrGccCitizenSponsores,
    countriesOfDisastersInvestor,
    ResidencyForNewBornAndSponsorTypeInvestor,
    issueResidence,
    renewResidence
  ]

  if (residentServices.includes(serviceId)) {
    formStore.form.setFieldValue && formStore.form.setFieldValue('sponsorTypeId', resident)
    formStore.extraMap.disableSponsorType = true
    return options.filter((o) => o.id == resident)
  } else if (typingCenterServices.includes(serviceId)) {
    return options.filter(
      (o) =>
        ![
          SponsorCompany,
          SponsorUnionGovernment,
          SponsorLocalGovernment,
          SponsorSemiGovernment,
          AnimalitySponsor,
          AgriculturalSponsor,
          FreeZone
        ].includes(o.id)
    )
  }

  return options
}
//#endregion
async function filterSponsorDepartment(options) {
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )
  const {
    virtualResidencyServices,
    mainDepartments: { dubai }
  } = client_lookups
  if (virtualResidencyServices.includes(serviceId)) {
    return options.filter((d) => d.id != dubai)
  }
  return options
}

async function filterTCCurrentNationality(options) {
  const formStore = useFormStore()
  const { requestNumber = '' } = formStore?.draft || {}
  const serviceId = +(
    router.currentRoute?.value?.params?.serviceId ||
    useGlobalStore().startService.serviceTransactionId
  )

  const {
    ServiceTransactions: {
      isCountriesofDisastersService,
      renewCountriesofDisastersService,
      newBornForCountriesofDisastersService
    },
    nationality: { ukraine, sudan, afghanistan }
  } = client_lookups
  let _options = options
  if ([isCountriesofDisastersService, newBornForCountriesofDisastersService].includes(serviceId)) {
    _options = options.filter((d) => [ukraine, sudan].includes(d.id))
  }
  if (renewCountriesofDisastersService == serviceId) {
    _options = options.filter((d) => [ukraine, afghanistan].includes(d.id))
  }
  if (!requestNumber) {
    formStore.draft.request.currentNationalityId = _options?.length == 1 ? _options[0].id : null
  }
  return _options
}

async function onTCSponsorTypeIdChange(curObj) {
  const formStore = useFormStore()
  const { sponsorTypeId = '' } = formStore.form.values
  const {
    sponsorType: { diplomatic }
  } = client_lookups
  const { isEstablishmentSponsor } = curObj
  formStore.extraMap.isEIDAholderSponsor = true
  if (sponsorTypeId == diplomatic || !isEstablishmentSponsor) {
    formStore.extraMap.isEIDAholderSponsor = false
  }
}

async function checkIfVirtualSponsorByUniNumOrFileNumber() {
  const formStore = useFormStore()
  const {
    params: { serviceId }
  } = router.currentRoute.value
  const { personUnifiedNumber, resiDepartmentCode, resiYear, resiServiceCode, resiSequenceNumber } =
    formStore.form.values || formStore.draft
  const { typingCenterResidencyServicesWithoutSponsor } = client_config

  const params = {
    unifiedNumber: personUnifiedNumber,
    moduleId: 1,
    FileStatusId: 1,
    serviceTransactionId: serviceId,
    ...(resiDepartmentCode && { departmentCode: resiDepartmentCode }),
    ...(resiYear && { resiYear }),
    ...(resiServiceCode && { resiServiceCode }),
    ...(resiSequenceNumber && { resiSequenceNumber })
  }

  const response = await axiosInstance.get('/api/user/checkIfVirtualSponsorByUniNumOrFileNumber', {
    params
  })

  const isResidency = typingCenterResidencyServicesWithoutSponsor.includes(+serviceId)
  Object.assign(formStore.extraMap, {
    hideSponsorInformation: isResidency || response.isVirtualSponsor
  })
}

/**
 * used in
 * role: typing center
 * service: [57] RESIDENCY - FAMILY MEMBERS OF THE SPONSOR - WORKING IN PUBLIC SECTOR - ISSUE RESIDENCE
 */
export default function () {
  return {
    processBeneficiaryUnifiedNumber57,
    checkExceptionalCase,
    filterDivorcedRelations,
    handleFamilyRelationChange57,
    checkPersonInfo57: checkPersonInfo,
    setSponsorType57: setSponsorType,
    filterSponsorDepartment,
    filterTCCurrentNationality,
    onTCSponsorTypeIdChange
  }
}
